import React, { useEffect, useState } from "react";
import { Plus, Receipt, FileText, AlertTriangle, Loader2, ArrowUpRight, ArrowDownRight, Trash2, Pencil, ScrollText } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, trDate, formatApiError, weekendRow } from "@/lib/api";
import { Card } from "@/components/Cards";
import StatementExport from "@/components/StatementExport";
import ShareHistory from "@/components/ShareHistory";
import MonthlyInvoiceDialog from "@/components/MonthlyInvoiceDialog";
import BulkInvoiceDialog from "@/components/BulkInvoiceDialog";
import InvoiceDialog from "@/components/InvoiceDialog";
import InvoiceDetailDialog from "@/components/InvoiceDetailDialog";
import SearchBox, { matches } from "@/components/SearchBox";
import AccountSelect from "@/components/AccountSelect";
import { takeJump } from "@/lib/jump";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Ledger() {
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState(() => takeJump("ledger")?.id || "ALL");
  const [search, setSearch] = useState("");
  const [allTxns, setTxns] = useState([]);
  const txns = allTxns.filter((t) => matches(search, t.customer_name, t.description, t.method, t.invoice_no, t.amount));
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_id: "", amount: "", method: "Nakit", description: "", date: todayISO(), account_id: "" });
  const [payMode, setPayMode] = useState("cash");
  const [cheque, setCheque] = useState({ bank: "", cheque_no: "", due_date: todayISO() });
  const [shareTick, setShareTick] = useState(0);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [detail, setDetail] = useState(null);
  const hasVat = txns.some((t) => t.vat > 0);

  const loadCustomers = async () => {
    const { data } = await api.get("/customers");
    setCustomers(data);
  };
  const loadTxns = async () => {
    const params = filter !== "ALL" ? { customer_id: filter } : {};
    const { data } = await api.get("/ledger", { params });
    setTxns(data);
  };

  useEffect(() => { loadCustomers(); }, []);
  useEffect(() => { loadTxns(); /* eslint-disable-next-line */ }, [filter]);

  const openPayment = () => {
    setForm((f) => ({ customer_id: filter !== "ALL" ? filter : (customers[0]?.id || ""), amount: "",
      method: f.method || "Nakit", description: "", date: todayISO(), account_id: f.account_id || "" }));
    setPayMode("cash");
    setCheque({ bank: "", cheque_no: "", due_date: todayISO() });
    setOpen(true);
  };

  const savePayment = async () => {
    if (!form.customer_id || !(parseFloat(form.amount) > 0)) { toast.error("Müşteri ve tutar gerekli"); return; }
    setSaving(true);
    try {
      if (payMode === "cheque") {
        await api.post("/cashbook/cheques", { direction: "in", customer_id: form.customer_id, amount: parseFloat(form.amount),
          bank: cheque.bank, cheque_no: cheque.cheque_no, due_date: cheque.due_date || null, note: form.description });
        toast.success("Çek portföye eklendi, cari güncellendi");
      } else {
        await api.post("/ledger/payment", { ...form, amount: parseFloat(form.amount) });
        toast.success("Tahsilat kaydedildi");
      }
      setOpen(false); loadTxns(); loadCustomers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/ledger/${id}`); toast.success("İşlem silindi"); loadTxns(); loadCustomers(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totalDebit = txns.filter((t) => t.type === "sale").reduce((s, t) => s + t.amount, 0);
  const totalCredit = txns.filter((t) => t.type === "payment").reduce((s, t) => s + t.amount, 0);
  const balance = totalDebit - totalCredit;
  const overdueTotal = txns.filter((t) => t.overdue).reduce((s, t) => s + t.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Cari Hesaplar & Tahsilat</h1>
          <p className="text-sm text-muted-foreground mt-1">Borç, tahsilat, vade ve hareket geçmişi ekstresi.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <BulkInvoiceDialog onCreated={() => { loadTxns(); loadCustomers(); }} />
          <Button data-testid="ledger-add-invoice-btn" onClick={() => setInvoiceOpen(true)} variant="outline" className="rounded-full h-11 px-5 font-semibold gap-2">
            <FileText className="h-4 w-4" /> Satış Faturası
          </Button>
          <Button data-testid="ledger-add-payment-btn" onClick={openPayment} className="rounded-full h-11 px-6 font-semibold gap-2">
            <Plus className="h-4 w-4" /> Tahsilat Ekle
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-11 w-full sm:w-[280px]" data-testid="ledger-filter-customer-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tüm Müşteriler</SelectItem>
            {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <SearchBox value={search} onChange={setSearch} placeholder="Müşteri, açıklama, fatura no ara…" testId="ledger-search-input" className="w-full sm:w-[300px]" />
        {filter !== "ALL" ? (
          <>
            <MonthlyInvoiceDialog customerId={filter} customer={customers.find((c) => c.id === filter)} onCreated={() => { loadTxns(); loadCustomers(); }} />
            <StatementExport customerId={filter} customer={customers.find((c) => c.id === filter)} onShared={() => setShareTick((t) => t + 1)} />
          </>
        ) : (
          <div className="text-xs text-muted-foreground self-center" data-testid="export-hint">Ekstre indirmek için bir müşteri seçin</div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat label="Toplam Borç" value={TRY(totalDebit)} color="text-rose-400" />
        <MiniStat label="Toplam Tahsilat" value={TRY(totalCredit)} color="text-emerald-400" />
        <MiniStat label="Kalan Bakiye" value={TRY(balance)} color="text-amber-400" />
        <MiniStat label="Vadesi Geçen" value={TRY(overdueTotal)} color="text-rose-400" icon={AlertTriangle} />
      </div>

      {filter !== "ALL" && <ShareHistory customerId={filter} refreshKey={shareTick} />}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                {["Tarih", "Müşteri", "İşlem", "Yöntem", "Vade", ...(hasVat ? ["Net", "KDV"] : []), hasVat ? "Borç (Brüt)" : "Borç", "Tahsilat", ""].map((h, i, arr) => (
                  <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${i >= 5 && i < arr.length - 1 ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txns.length === 0 ? (
                <tr><td colSpan={hasVat ? 10 : 8} className="px-4 py-12 text-center text-muted-foreground">İşlem bulunamadı</td></tr>
              ) : txns.map((t) => (
                <tr key={t.id} data-testid={`ledger-row-${t.id}`} onClick={() => t.lines && setDetail(t)} className={"border-b border-border/60 hover:bg-accent/40 transition-colors" + (t.lines ? " cursor-pointer" : "") + weekendRow(t.date)}>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{trDate(t.date)}</td>
                  <td className="px-4 py-3 font-medium">{t.customer_name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${t.type === "sale" ? "text-rose-400" : "text-emerald-400"}`}>
                      {t.type === "sale" ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                      {t.description}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{t.method || "—"}</td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {t.type === "sale" && t.due_date ? (
                      <span className={t.overdue ? "text-rose-400 font-semibold" : "text-muted-foreground"}>
                        {trDate(t.due_date)}{t.overdue && " ⚠"}
                      </span>
                    ) : "—"}
                  </td>
                  {hasVat && <td className="px-4 py-3 text-right font-mono text-muted-foreground" data-testid={`ledger-net-${t.id}`}>{t.type === "sale" ? TRY(t.net) : "—"}</td>}
                  {hasVat && <td className="px-4 py-3 text-right font-mono text-amber-400" data-testid={`ledger-vat-${t.id}`}>{t.type === "sale" && t.vat ? TRY(t.vat) : "—"}</td>}
                  <td className="px-4 py-3 text-right font-mono">{t.type === "sale" ? TRY(t.amount) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-400">{t.type === "payment" ? TRY(t.amount) : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {t.type === "sale" && t.lines && (
                      <button onClick={() => { setEditInvoice(t); setInvoiceOpen(true); }} data-testid={`ledger-edit-${t.id}`} title="Faturayı düzenle"
                        className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground hover:text-foreground mr-1">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => del(t.id)} data-testid={`ledger-delete-${t.id}`}
                      className="h-7 w-7 rounded-md hover:bg-destructive/10 inline-flex items-center justify-center text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" /> Tahsilat Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <F label="Müşteri">
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="payment-customer-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.short_name || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <div className="inline-flex rounded-xl bg-muted/50 p-1 w-full" data-testid="payment-mode-tabs">
              <button type="button" onClick={() => setPayMode("cash")} data-testid="payment-mode-cash" className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-colors ${payMode !== "cheque" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Nakit / Havale</button>
              <button type="button" onClick={() => setPayMode("cheque")} data-testid="payment-mode-cheque" className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-colors inline-flex items-center justify-center gap-1.5 ${payMode === "cheque" ? "bg-card shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}><ScrollText className="h-3.5 w-3.5" /> Çek ile</button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <F label="Tutar (₺)">
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="payment-amount" className="font-mono" placeholder="0" />
              </F>
              <F label={payMode === "cheque" ? "Vade Tarihi" : "Tarih"}>
                {payMode === "cheque"
                  ? <Input type="date" value={cheque.due_date} onChange={(e) => setCheque({ ...cheque, due_date: e.target.value })} data-testid="payment-cheque-due" className="font-mono" />
                  : <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date" className="font-mono" />}
              </F>
            </div>
            {payMode === "cheque" ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <F label="Banka"><Input value={cheque.bank} onChange={(e) => setCheque({ ...cheque, bank: e.target.value })} data-testid="payment-cheque-bank" placeholder="Örn: Ziraat" /></F>
                  <F label="Çek No"><Input value={cheque.cheque_no} onChange={(e) => setCheque({ ...cheque, cheque_no: e.target.value })} data-testid="payment-cheque-no" placeholder="0012345" className="font-mono" /></F>
                </div>
                <F label="Not"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="payment-description" placeholder="Opsiyonel" /></F>
                <p className="text-xs text-teal-400">Çek portföye eklenecek ve cari bakiyeden düşülecek. Nakit, çek tahsil edildiğinde kasaya girer.</p>
              </>
            ) : (
              <>
                <F label="Hesap (Kasa / Banka)">
                  <AccountSelect value={form.account_id} onChange={(id, a) => setForm({ ...form, account_id: id, method: a?.name || form.method })} testId="payment-method-select" />
                </F>
                <F label="Açıklama">
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="payment-description" placeholder="Opsiyonel" />
                </F>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={savePayment} disabled={saving} data-testid="payment-save" className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {payMode === "cheque" ? "Çeki Kaydet" : "Tahsilatı Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoiceDetailDialog invoice={detail} partyName={customers.find((c) => c.id === detail?.customer_id)?.name || detail?.customer_name} onClose={() => setDetail(null)} onEdit={(t) => { setEditInvoice(t); setInvoiceOpen(true); }} />
      <InvoiceDialog open={invoiceOpen} onClose={() => { setInvoiceOpen(false); setEditInvoice(null); }} kind="sale" parties={customers} defaultPartyId={filter !== "ALL" ? filter : ""} editItem={editInvoice} onSaved={() => { loadTxns(); loadCustomers(); }} />
    </div>
  );
}

const MiniStat = ({ label, value, color, icon: Icon }) => (
  <Card className="p-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
      {Icon && <Icon className="h-3.5 w-3.5" />} {label}
    </div>
    <div className={`mt-2 font-mono text-lg sm:text-xl font-bold ${color}`}>{value}</div>
  </Card>
);

const F = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
